import { useEffect, useState } from "react";
import { Award, ExternalLink, Copy, Check, ShieldCheck, FileDown, Mail } from "lucide-react";
import api, { API } from "../lib/api";
import TopNav from "../components/TopNav";
import EmailPackModal from "../components/EmailPackModal";
import { useAuth } from "../lib/auth";

export default function Badges() {
  const { user } = useAuth();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/me/badges");
        setBadges(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyUrl = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard blocked; user can still right-click the share button */
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">// CREDENTIAL_WALLET</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight mt-2">
              Verifiable Badges
            </h1>
            <p className="text-[var(--text-2)] text-sm mt-3 max-w-3xl">
              Every credential earned at the Academy is issued as a cryptographically-signed
              Open Badge. Recipients can post the verification URL to LinkedIn or share with
              employers &mdash; signatures stay valid as long as the badge has not been revoked.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <a
              href={`${API}/me/compliance-pack.pdf`}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="download-compliance-pack"
            >
              <FileDown size={14} /> Download Compliance Pack
            </a>
            <button
              onClick={() => setEmailOpen(true)}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="email-compliance-pack"
            >
              <Mail size={14} /> Email to contact
            </button>
          </div>
        </div>

        <EmailPackModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          endpoint="/me/compliance-pack/email"
          subjectName={user?.name || "My training record"}
        />

        {loading && <div className="text-[var(--text-2)] font-mono text-sm">// Loading wallet…</div>}

        {!loading && badges.length === 0 && (
          <div className="flat-card p-8 text-center" data-testid="badges-empty">
            <Award size={32} className="mx-auto mb-3" color="var(--text-2)" strokeWidth={1.2} />
            <div className="overline mb-2">// NO_BADGES_ISSUED</div>
            <p className="text-[var(--text-2)] text-sm">
              Complete a certification course to earn your first verifiable badge.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4" data-testid="badge-grid">
          {badges.map((b) => (
            <div key={b.badge_id} className="flat-card p-5 relative overflow-hidden" data-testid={`badge-${b.badge_id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={20} color="var(--cyan)" strokeWidth={1.5} />
                  <div className="overline text-[var(--cyan)]">// VERIFIED</div>
                </div>
                <span className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] px-2 py-0.5 border border-[var(--border)]">
                  {b.code || "BADGE"}
                </span>
              </div>

              <h3 className="font-display font-bold text-lg uppercase tracking-tight text-white leading-tight mb-3">
                {b.title || "DefDossier Credential"}
              </h3>

              <div className="font-mono text-xs text-[var(--text-2)] space-y-1 mb-4 border-l-2 border-[var(--border)] pl-3">
                <div>ISSUED &nbsp;{b.issued_at ? new Date(b.issued_at).toLocaleDateString() : "—"}</div>
                <div>EXPIRES {b.expires_at ? new Date(b.expires_at).toLocaleDateString() : "Never"}</div>
                <div className="truncate">BADGE&nbsp;ID&nbsp;<span className="text-[var(--cyan)]">{b.badge_id}</span></div>
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  href={b.linkedin_share_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
                  data-testid={`linkedin-${b.badge_id}`}
                >
                  Add to LinkedIn <ExternalLink size={11} />
                </a>
                <a
                  href={b.verification_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
                  data-testid={`verify-link-${b.badge_id}`}
                >
                  View public page <ExternalLink size={11} />
                </a>
                <button
                  onClick={() => copyUrl(b.verification_url, b.badge_id)}
                  className="btn-ghost inline-flex items-center gap-1.5 text-xs px-3 py-1.5"
                  data-testid={`copy-${b.badge_id}`}
                >
                  {copied === b.badge_id ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy URL</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
